import React, { useState, useEffect } from 'react';
import { Key, Save, Eye, EyeOff, X, AlertTriangle } from 'lucide-react';

const ApiKeyModal = ({ onClose, onSave }) => {
  const [apiKeys, setApiKeys] = useState({
    kucoin_api_key: '',
    kucoin_api_secret: '',
    kucoin_api_passphrase: ''
  });
  const [showSecrets, setShowSecrets] = useState({
    key: false,
    secret: false,
    passphrase: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Load existing keys on mount
  useEffect(() => {
    const savedKeys = localStorage.getItem('kucoin_api_keys');
    if (savedKeys) {
      try {
        const keys = JSON.parse(savedKeys);
        setApiKeys({
          kucoin_api_key: keys.kucoin_api_key || '',
          kucoin_api_secret: keys.kucoin_api_secret || '',
          kucoin_api_passphrase: keys.kucoin_api_passphrase || ''
        });
      } catch (e) {
        console.error('Failed to load saved keys');
      }
    }
  }, []);

  const handleSave = async () => {
    if (!apiKeys.kucoin_api_key || !apiKeys.kucoin_api_secret || !apiKeys.kucoin_api_passphrase) {
      setMessage({ type: 'error', text: 'Please fill all fields' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Save to localStorage
      localStorage.setItem('kucoin_api_keys', JSON.stringify(apiKeys));
      setMessage({ type: 'success', text: '✅ Futures API keys saved! Now click "Go Live" to start live trading.' });
      
      setTimeout(() => {
        onSave && onSave();
        onClose();
      }, 2000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save API keys' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-700 p-6 flex items-center justify-between sticky top-0 bg-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-purple-400">⚡ KuCoin FUTURES API Keys</h2>
            <p className="text-sm text-gray-400 mt-1">Required for live futures trading</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Important Warning */}
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-400 flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-red-400 font-bold mb-2">⚠️ IMPORTANT: You need FUTURES API Keys!</p>
                <p className="text-sm text-gray-300">
                  Regular KuCoin Spot API keys will NOT work. You must create API keys specifically for <strong>KuCoin Futures</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* How to get Futures API */}
          <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-4">
            <p className="text-purple-400 font-semibold mb-3">📝 How to get KuCoin FUTURES API Keys:</p>
            <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside">
              <li>Go to <a href="https://www.kucoin.com/futures" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline font-semibold">KuCoin Futures</a> (NOT regular KuCoin!)</li>
              <li>Login → Click your profile → <strong>API Management</strong></li>
              <li>Click <strong>"Create API"</strong> under Futures section</li>
              <li>Enable permissions: <strong>General + Futures Trading</strong></li>
              <li>Set IP restriction to <strong>"No"</strong> or add server IP</li>
              <li>Create a <strong>passphrase</strong> (remember it!)</li>
              <li>Copy: <strong>API Key</strong>, <strong>API Secret</strong>, <strong>Passphrase</strong></li>
            </ol>
          </div>

          {/* Transfer Funds Notice */}
          <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
            <p className="text-yellow-400 font-semibold mb-2">💰 Don't forget to transfer funds!</p>
            <p className="text-sm text-gray-300">
              Transfer USDT from your <strong>Main Account</strong> to your <strong>Futures Account</strong> on KuCoin before trading.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Key size={16} className="inline mr-2" />
                Futures API Key
              </label>
              <div className="relative">
                <input
                  type={showSecrets.key ? 'text' : 'password'}
                  value={apiKeys.kucoin_api_key}
                  onChange={(e) => setApiKeys({ ...apiKeys, kucoin_api_key: e.target.value })}
                  className="w-full px-4 py-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  placeholder="Enter your KuCoin Futures API Key"
                  data-testid="api-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, key: !showSecrets.key })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showSecrets.key ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Key size={16} className="inline mr-2" />
                Futures API Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.secret ? 'text' : 'password'}
                  value={apiKeys.kucoin_api_secret}
                  onChange={(e) => setApiKeys({ ...apiKeys, kucoin_api_secret: e.target.value })}
                  className="w-full px-4 py-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  placeholder="Enter your KuCoin Futures API Secret"
                  data-testid="api-secret-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, secret: !showSecrets.secret })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showSecrets.secret ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Key size={16} className="inline mr-2" />
                Futures API Passphrase
              </label>
              <div className="relative">
                <input
                  type={showSecrets.passphrase ? 'text' : 'password'}
                  value={apiKeys.kucoin_api_passphrase}
                  onChange={(e) => setApiKeys({ ...apiKeys, kucoin_api_passphrase: e.target.value })}
                  className="w-full px-4 py-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  placeholder="Enter your KuCoin Futures API Passphrase"
                  data-testid="api-passphrase-input"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, passphrase: !showSecrets.passphrase })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showSecrets.passphrase ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>

          {message.text && (
            <div className={`rounded-lg p-4 ${message.type === 'success' ? 'bg-green-900/30 border border-green-700 text-green-400' : 'bg-red-900/30 border border-red-700 text-red-400'}`}>
              {message.text}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={loading}
              data-testid="save-api-keys-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
            >
              <Save size={20} />
              {loading ? 'Saving...' : 'Save Futures API Keys'}
            </button>
            <button
              onClick={onClose}
              className="px-6 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition"
            >
              Cancel
            </button>
          </div>

          {/* Next Steps */}
          <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <p className="text-green-400 font-semibold mb-2">✅ After saving API keys:</p>
            <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
              <li>Close this modal</li>
              <li>Click <strong>"Go Live"</strong> button to switch from Paper to Live mode</li>
              <li>Click <strong>"Stop Bot"</strong> then <strong>"Start Bot"</strong> to restart with live keys</li>
              <li>You'll receive a Telegram notification when live trading starts</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyModal;
